import React, { useEffect } from "react";
import { useCatalogResource } from "../hooks/useCatalogResource.js";
import SectionFooter from "../shared/SectionFooter.jsx";

const RESERVED_GROUPS = [
  {
    key: "session_required",
    label: "Requieren sesión",
    description: "El bot pide identificación antes de resolver estos intents.",
  },
  {
    key: "auto_handoff",
    label: "Derivación automática",
    description: "Estos intents derivan directamente a un agente.",
  },
  {
    key: "exact_match",
    label: "Coincidencia exacta",
    description: "Se evalúan con reglas exactas, sin clasificación flexible.",
  },
];

const RESERVED_GROUP_KEYS = new Set(RESERVED_GROUPS.map((group) => group.key));

function parseTextList(rawValue) {
  const raw = String(rawValue || "");
  const entries = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  // Preserve trailing comma while typing in controlled inputs.
  if (/,\s*$/.test(raw)) {
    entries.push("");
  }

  return entries;
}

function stringifyFlowTarget(target) {
  if (Array.isArray(target)) return target.join(", ");
  if (typeof target === "string") return target;
  return "";
}

export default function IntentsSection({ initialData, loadKey, onDirtyChange, onToast, onResourceSaved }) {
  const { data, dirty, saving, loading, error, update, save, discard, reload } =
    useCatalogResource("intents", initialData ?? {}, loadKey);

  useEffect(() => { onDirtyChange?.("intents", dirty); }, [dirty, onDirtyChange]);

  const allData = data && typeof data === "object" ? data : {};
  const fixedGroups = RESERVED_GROUPS.map((group) => ({
    ...group,
    intents: Array.isArray(allData[group.key]) ? allData[group.key] : [],
  }));
  const customGroups = Object.entries(allData).filter(
    ([key, value]) => key !== "flow_map" && !RESERVED_GROUP_KEYS.has(key) && Array.isArray(value)
  );
  const flowMap = allData.flow_map && typeof allData.flow_map === "object" && !Array.isArray(allData.flow_map)
    ? allData.flow_map
    : {};
  const flowEntries = Object.entries(flowMap);

  function addGroup() {
    update((current) => {
      const next = { ...(current || {}) };
      let c = Object.keys(next).length + 1;
      let k = `grupo_${c}`;
      while (next[k]) { c += 1; k = `grupo_${c}`; }
      return { [k]: [], ...next };
    });
  }
  function renameGroup(oldKey, newKey) {
    const candidate = String(newKey || "").trim();
    if (candidate && RESERVED_GROUP_KEYS.has(candidate)) {
      onToast?.("Ese nombre está reservado para un comportamiento fijo");
      return;
    }
    update((current) => {
      const next = { ...(current || {}) };
      const value = next[oldKey];
      delete next[oldKey];
      next[newKey.trim() || oldKey] = value;
      return next;
    });
  }
  function removeGroup(key) {
    update((current) => { const next = { ...(current || {}) }; delete next[key]; return next; });
  }
  function updateIntents(group, rawValue) {
    update((current) => ({
      ...(current || {}),
      [group]: parseTextList(rawValue),
    }));
  }

  function updateFlowMap(updater) {
    update((current) => {
      const base = current && typeof current === "object" ? { ...current } : {};
      const currentMap = base.flow_map && typeof base.flow_map === "object" && !Array.isArray(base.flow_map)
        ? { ...base.flow_map }
        : {};
      base.flow_map = updater(currentMap);
      return base;
    });
  }

  function addFlowEntry() {
    updateFlowMap((currentMap) => {
      let c = Object.keys(currentMap).length + 1;
      let key = `intent_${c}`;
      while (currentMap[key]) { c += 1; key = `intent_${c}`; }
      return { ...currentMap, [key]: "" };
    });
  }

  function renameFlowIntent(oldIntent, newIntent) {
    updateFlowMap((currentMap) => {
      const next = { ...currentMap };
      const value = next[oldIntent];
      delete next[oldIntent];
      const finalKey = String(newIntent || "").trim() || oldIntent;
      next[finalKey] = value;
      return next;
    });
  }

  function updateFlowTarget(intent, rawValue) {
    updateFlowMap((currentMap) => {
      const next = { ...currentMap };
      const entries = parseTextList(rawValue);
      if (entries.length === 0) {
        next[intent] = "";
      } else if (entries.length === 1) {
        next[intent] = entries[0];
      } else {
        next[intent] = entries;
      }
      return next;
    });
  }

  function removeFlowEntry(intent) {
    updateFlowMap((currentMap) => {
      const next = { ...currentMap };
      delete next[intent];
      return next;
    });
  }

  async function handleSave() {
    const ok = await save(() => onResourceSaved?.("intents"));
    if (ok) onToast?.("Intents guardados");
  }

  return (
    <div className="catalog-section">
      <div className="kb-section-header">
        <div>
          <h3>Comportamientos especiales</h3>
          <p>Estos grupos son fijos del sistema y no se pueden eliminar.</p>
        </div>
      </div>
      <div className="kb-grid">
        {fixedGroups.map((group) => (
          <div key={group.key} className="kb-row vertical">
            <div className="row-header">
              <label>{group.label}</label>
            </div>
            <small>{group.description}</small>
            <label className="full-width">
              Intents (separados por coma)
              <textarea
                rows={3}
                value={(group.intents || []).join(", ")}
                onChange={(e) => updateIntents(group.key, e.target.value)}
              />
            </label>
          </div>
        ))}
      </div>

      <div className="kb-section-header">
        <div>
          <h3>Grupos personalizados</h3>
          <p>Grupos opcionales para reglas o filtros adicionales.</p>
        </div>
        <button className="button-secondary" onClick={addGroup}>Añadir grupo</button>
      </div>
      <div className="kb-grid">
        {customGroups.map(([key, intents], i) => (
          <div key={`${key}-${i}`} className="kb-row vertical">
            <div className="row-header">
              <label>Grupo<input value={key} onChange={(e) => renameGroup(key, e.target.value)} /></label>
              <button className="button-secondary" onClick={() => removeGroup(key)}>Quitar</button>
            </div>
            <label className="full-width">
              Intents (separados por coma)
              <textarea rows={3} value={(intents || []).join(", ")} onChange={(e) => updateIntents(key, e.target.value)} />
            </label>
          </div>
        ))}
        {!customGroups.length && <p className="kb-empty">No hay grupos personalizados.</p>}
      </div>

      <div className="kb-section-header">
        <div>
          <h3>Inicio de flujos por intent</h3>
          <p>Mapeo de intent a flujo. Si hay varios flujos, separalos por coma.</p>
        </div>
        <button className="button-secondary" onClick={addFlowEntry}>Añadir mapeo</button>
      </div>
      <div className="kb-grid">
        {flowEntries.map(([intent, target], i) => (
          <div key={`${intent}-${i}`} className="kb-row vertical">
            <div className="row-header">
              <label>Intent<input value={intent} onChange={(e) => renameFlowIntent(intent, e.target.value)} /></label>
              <button className="button-secondary" onClick={() => removeFlowEntry(intent)}>Quitar</button>
            </div>
            <label className="full-width">
              Flujo destino (uno o varios)
              <input
                value={stringifyFlowTarget(target)}
                onChange={(e) => updateFlowTarget(intent, e.target.value)}
                placeholder="coverage_check o diag_sin_servicio_ftth, diag_sin_servicio_hfc"
              />
            </label>
          </div>
        ))}
        {!flowEntries.length && <p className="kb-empty">No hay mapeos de flujo configurados.</p>}
      </div>

      <SectionFooter
        dirty={dirty} saving={saving} loading={loading} error={error}
        onSave={handleSave} onDiscard={discard} onReload={reload}
      />
    </div>
  );
}
